import { FC, useMemo } from 'react';
import { max } from 'd3';

import {
  Accessor,
  CategoryAxis,
  categoryScale,
  Dots,
  InteractionItem,
  LinearAxis,
  linearScale,
  Plot,
  TooltipCallbackArgs,
} from '@dbt-labs/sourdough';

import { RunStatus } from '@/__generated__/graphql';
import { useModelExecutionRunInfo } from '@/hooks/discovery/modelExecutionRunInfo';
import { JobFilter } from '@/pages/account/project/resource/details/components/Tabs/PerformanceView';
import {
  addDays,
  formatDateUtcYYYYMMDD,
  getISODateUTCArrayFromDateStrings,
} from '@/util/date';
import { generateDurationFormatter } from '@/util/formatDuration';
import { ChartWrapper } from './ChartWrapper';
import { ModelExecutionGridlines } from './ModelExecutionGridLines';

export interface AverageModelExecutionChartProps {
  environmentId: number;
  startDate: string;
  endDate: string;
  uniqueId: string;
  setOldestDate: (date: Date | undefined) => void;
  onItemClicked?: (filter: JobFilter) => void;
  selectedDate?: string;
}

export type ModelExecution = {
  date: string;
  meanExecutionTime: number;
  executionTimes: number[];
};

export const AverageModelExecutionChart: FC<AverageModelExecutionChartProps> = ({
  environmentId,
  startDate,
  endDate,
  uniqueId,
  setOldestDate,
  onItemClicked = () => {},
  selectedDate,
}) => {
  const [executionRunInfo] = useModelExecutionRunInfo({
    environmentId: environmentId,
    startDate: startDate,
    // API is end date exclusive, so we add 1 day to the end date
    endDate: formatDateUtcYYYYMMDD(addDays(new Date(endDate), 1)),
    uniqueId: uniqueId,
  });
  setOldestDate(undefined);
  const rawData = executionRunInfo.data?.performance?.modelExecutionHistory;

  // Aggregate execution data, filtering out reused runs
  const data: ModelExecution[] = useMemo(() => {
    if (!rawData?.length) return [];
    return rawData.map((day) => {
      const nonReusedTimes = (day.executionsByJob ?? [])
        .flatMap((job) => job.executions ?? [])
        .filter((exec) => exec.status !== RunStatus.Reused)
        .map((exec) => exec.executionTime);

      const meanExecutionTime =
        nonReusedTimes.length > 0
          ? nonReusedTimes.reduce((sum, t) => sum + t, 0) / nonReusedTimes.length
          : 0;

      return {
        date: day.date,
        meanExecutionTime,
        executionTimes: nonReusedTimes,
      };
    });
  }, [rawData]);

  const { paddedData, maxTimeSec, formatDuration } = useMemo(() => {
    const dateArray = getISODateUTCArrayFromDateStrings(startDate, endDate);
    if (!data.length) {
      return {
        paddedData: [] as ModelExecution[],
        maxTimeSec: 0,
        formatDuration: generateDurationFormatter('s', 0),
      };
    }
    const dayMap = data.reduce<Record<string, ModelExecution | undefined>>(
      (acc, curr) => {
        acc[curr.date] = curr;
        return acc;
      },
      {},
    );

    const maxTimeSec = max(dateArray, (d) => dayMap[d]?.meanExecutionTime ?? 0) || 0;
    const formatDuration = generateDurationFormatter('s', maxTimeSec);

    const paddedData = dateArray.map((date) => {
      const existing = dayMap[date];
      if (existing) {
        // Hide dot for days where all runs were reused (no non-reused executions)
        if (existing.executionTimes.length === 0) {
          return {
            ...existing,
            meanExecutionTime: formatDuration.fromMaxUnits(-200, 's'),
          };
        }
        return existing;
      }
      return {
        date,
        meanExecutionTime: formatDuration.fromMaxUnits(-200, 's'), // there is no way to hide dots that have no data. This is a hack to hide them
        executionTimes: [],
        executionsByJob: [],
      };
    }) as ModelExecution[];

    return {
      paddedData,
      maxTimeSec,
      formatDuration: formatDuration,
    };
  }, [data, startDate, endDate]);

  const averageExecutionTimeSeconds = useMemo(() => {
    const executions = data.flatMap((d) => d.executionTimes).map((x) => (!x ? 0 : x));

    return executions.length > 0
      ? executions.reduce((acc, curr) => acc + curr, 0) / executions.length
      : 0;
  }, [data]);

  const selectedDayExecution = useMemo(() => {
    if (!selectedDate) return undefined;
    return data.find((d) => d.date === selectedDate);
  }, [data, selectedDate]);

  const displayExecutionTime = selectedDayExecution
    ? selectedDayExecution.meanExecutionTime
    : averageExecutionTimeSeconds;
  const displayLabel = selectedDayExecution
    ? `Avg. execution time on ${selectedDate} (excludes reused)`
    : 'Avg. execution time (excludes reused)';

  const xAccessor: Accessor<ModelExecution, string> = (x: ModelExecution) => {
    const [, MM, DD] = x.date.split('-');
    return `${MM}-${DD}`;
  };
  const yAccessor = (d: ModelExecution) =>
    formatDuration.toMaxUnits('s', d.meanExecutionTime);

  // Do not use timescale as it displays in local time which isn't calculable for UTC date strings
  const xScale = categoryScale({
    labels: paddedData?.map(xAccessor) ?? [],
  });
  const yScale = linearScale({
    min: 0,
    max: formatDuration.toMaxUnits('s', maxTimeSec),
  });

  return (
    <>
      <div className="ml-[10px]">
        <div className="font-sans text-sm font-light leading-6 text-fgDecorative">
          {displayLabel}
        </div>
        <span className="text-2xl">
          {formatDuration.humanize('s', displayExecutionTime)}
        </span>
      </div>
      <div className="h-[300px]">
        <ChartWrapper
          testId="average-model-execution-chart"
          numDataPoints={paddedData.length}
          noDataMessage="No data available"
          isLoading={executionRunInfo.fetching}
          error={executionRunInfo.error?.message}
          height={300}
          margin={{ bottom: 25, left: 45, right: 5, top: 15 }}
          xAccessor={xAccessor}
          yAccessor={yAccessor}
          xScale={xScale}
          yScale={yScale}
          tooltip={({ interactionItem }: TooltipCallbackArgs<ModelExecution>) => {
            const item = interactionItem?.item;
            return (
              <div className="dark flex flex-col space-y-2">
                <div className="text-xs">
                  <button
                    className="block text-fgBrand"
                    onClick={() => {
                      if (item) {
                        onItemClicked?.(item);
                      }
                    }}
                  >
                    {item?.date}
                  </button>
                  <div>
                    AVG Execution Time:{' '}
                    {formatDuration.humanize('s', item?.meanExecutionTime || 0)}
                  </div>
                  <div>
                    Total Executions (excl. reused): {item?.executionTimes?.length || 0}
                  </div>
                </div>
              </div>
            );
          }}
          onItemClick={(item: InteractionItem<ModelExecution>) => {
            onItemClicked?.(item.item);
          }}
        >
          <ModelExecutionGridlines />
          <LinearAxis
            axis="y"
            label={`Execution Time (${formatDuration.unitsAbbrev})`}
          />
          <CategoryAxis axis="x" label="Execution Date (UTC)" />
          <Plot data={paddedData} itemComponent={Dots} />
        </ChartWrapper>
      </div>
      <p className="mt-1 text-xs text-fgDecorative">
        Click a data point to view runs for that date
      </p>
    </>
  );
};
